import { createContext, type PropsWithChildren, useContext, useMemo } from 'react';

import { useGetUniversesQuery } from '../queries/universes-queries';
import type { ChildcareUniverse, CleaningUniverse, Universe, UniverseReference } from '../types/commons';

type UniversesContextType = {
  universes: Universe[] | undefined;
  isLoadingUniverses: boolean;
  universesError: Error | null;
};

export const UniversesContext = createContext<UniversesContextType>({
  universes: undefined,
  isLoadingUniverses: true,
  universesError: null,
});

// This context provider is based on this doc: https://tkdodo.eu/blog/react-query-and-react-context
// Here we don't handle loading / error but we publish them. This is to avoid a waterfall effect between all the
// similar providers. It's up to the client code to have some loader component that will check the loading status
// of all these providers and make the app available only when they are loaded
export const UniversesProvider = ({ children }: PropsWithChildren) => {
  const { universes, universesError, isLoadingUniverses } = useGetUniversesQuery();

  const contextValue = useMemo(
    () => ({ universes, isLoadingUniverses, universesError }),
    [universes, isLoadingUniverses, universesError],
  );

  return <UniversesContext.Provider value={contextValue}>{children}</UniversesContext.Provider>;
};

export const useUniverses = () => {
  const { universes } = useContext(UniversesContext);

  if (!universes) throw Error('Universes should be defined');

  return universes;
};

export const useUniverse = (reference: UniverseReference) => {
  const universe = useUniverses().find((u) => u.reference === reference);

  if (!universe) throw Error(`Universe ${reference} should be defined`);

  return universe;
};

export const useChildcareUniverse = () => useUniverse('childcare') as ChildcareUniverse;

export const useCleaningUniverse = () => useUniverse('cleaning') as CleaningUniverse;
